<script setup>
import { ref, nextTick, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CopilotUploadsAPI from 'dashboard/api/captain/copilotUploads';

const emit = defineEmits(['send', 'generateImage']);
const { t } = useI18n();

const message = ref('');
const textareaRef = ref(null);
const fileInputRef = ref(null);
const attachedFiles = ref([]);
const isUploading = ref(false);
const isRecording = ref(false);
const speechSupported = ref(false);

let recognition = null;

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
const MAX_FILES = 5;

const adjustHeight = () => {
  if (!textareaRef.value) return;
  textareaRef.value.style.height = 'auto';
  textareaRef.value.style.height = `${textareaRef.value.scrollHeight}px`;
};

const hasContent = computed(() => {
  return message.value.trim() || attachedFiles.value.length > 0;
});

const canAttachMore = computed(() => {
  return attachedFiles.value.length < MAX_FILES;
});

// --- File handling ---
const isImageType = contentType => {
  return contentType && contentType.startsWith('image/');
};

const getFileIcon = contentType => {
  if (!contentType) return 'i-lucide-file';
  if (contentType.startsWith('image/')) return 'i-lucide-image';
  if (contentType.includes('pdf')) return 'i-lucide-file-text';
  if (contentType.includes('spreadsheet') || contentType.includes('csv') || contentType.includes('excel'))
    return 'i-lucide-file-spreadsheet';
  if (contentType.includes('word') || contentType.includes('document'))
    return 'i-lucide-file-text';
  if (contentType.startsWith('text/') || contentType.includes('json') || contentType.includes('xml'))
    return 'i-lucide-file-code';
  if (contentType.startsWith('audio/')) return 'i-lucide-file-audio';
  if (contentType.startsWith('video/')) return 'i-lucide-file-video';
  return 'i-lucide-file';
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const handleFileSelect = async event => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;

  const available = MAX_FILES - attachedFiles.value.length;
  const toUpload = files.slice(0, available);

  for (const file of toUpload) {
    if (file.size > MAX_FILE_SIZE) {
      // eslint-disable-next-line no-alert
      alert(t('CAPTAIN.COPILOT.FILE_INPUT.FILE_TOO_LARGE'));
      continue;
    }
    await uploadFile(file);
  }

  // Reset file input so same file can be re-selected
  if (fileInputRef.value) fileInputRef.value.value = '';
};

const uploadFile = async file => {
  isUploading.value = true;
  try {
    const response = await CopilotUploadsAPI.upload(file);
    const { url, filename, content_type, byte_size } = response.data;
    attachedFiles.value.push({
      url,
      filename,
      content_type,
      byte_size,
      // Create local preview URL for images
      previewUrl: isImageType(content_type) ? URL.createObjectURL(file) : null,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Copilot] File upload failed:', error);
    // eslint-disable-next-line no-alert
    alert(t('CAPTAIN.COPILOT.FILE_INPUT.UPLOAD_FAILED'));
  } finally {
    isUploading.value = false;
  }
};

const removeFile = index => {
  const file = attachedFiles.value[index];
  if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
  attachedFiles.value.splice(index, 1);
};

const removeAllFiles = () => {
  attachedFiles.value.forEach(file => {
    if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
  });
  attachedFiles.value = [];
};

const openFilePicker = () => {
  fileInputRef.value?.click();
};

// --- Speech recognition ---
const initSpeechRecognition = () => {
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    speechSupported.value = false;
    return;
  }
  speechSupported.value = true;

  recognition = new SpeechRecognition();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = document.documentElement.lang || 'pt-BR';

  // Text that was in the textarea before recording started
  let preRecordingText = '';
  // All finalized transcript segments accumulated during this recording session
  let finalTranscript = '';

  recognition.onresult = event => {
    let interimText = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const transcript = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        finalTranscript += transcript;
      } else {
        interimText += transcript;
      }
    }
    // Build message: original text + finalized speech + interim speech
    const base = preRecordingText ? preRecordingText + ' ' : '';
    message.value =
      base + finalTranscript + (interimText ? interimText : '');
    nextTick(adjustHeight);
  };

  recognition.onstart = () => {
    preRecordingText = message.value.trim();
    finalTranscript = '';
  };

  recognition.onend = () => {
    isRecording.value = false;
    nextTick(adjustHeight);
  };

  recognition.onerror = event => {
    // eslint-disable-next-line no-console
    console.error('[Copilot] Speech recognition error:', event.error);
    isRecording.value = false;
  };
};

const toggleRecording = () => {
  if (!recognition) return;

  if (isRecording.value) {
    recognition.stop();
    isRecording.value = false;
  } else {
    recognition.lang = document.documentElement.lang || 'pt-BR';
    recognition.start();
    isRecording.value = true;
  }
};

// --- Send ---
const sendMessage = () => {
  const text = message.value.trim();
  if (!text && attachedFiles.value.length === 0) return;
  if (isUploading.value) return;

  const payload = {
    message: text,
    attachments: attachedFiles.value.map(f => ({
      url: f.url,
      filename: f.filename,
      content_type: f.content_type,
    })),
  };

  emit('send', payload);
  message.value = '';
  removeAllFiles();
  nextTick(adjustHeight);
};

const handleInput = () => {
  nextTick(adjustHeight);
};

onMounted(() => {
  nextTick(adjustHeight);
  initSpeechRecognition();
});

onUnmounted(() => {
  if (recognition && isRecording.value) {
    recognition.stop();
  }
  // Clean up preview URLs
  attachedFiles.value.forEach(file => {
    if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
  });
});
</script>

<template>
  <div>
    <!-- Attached files list -->
    <div
      v-if="attachedFiles.length > 0"
      class="flex flex-wrap gap-1.5 mb-1.5 px-1"
    >
      <div
        v-for="(file, index) in attachedFiles"
        :key="index"
        class="group relative flex items-center gap-1.5 px-2 py-1 rounded-md bg-n-alpha-2 border border-n-weak text-xs text-n-slate-11 max-w-[180px]"
      >
        <!-- Image preview thumbnail -->
        <img
          v-if="file.previewUrl"
          :src="file.previewUrl"
          :alt="file.filename"
          class="size-5 rounded object-cover flex-shrink-0"
        />
        <i
          v-else
          :class="[getFileIcon(file.content_type), 'size-3.5 flex-shrink-0']"
        />
        <div class="flex flex-col min-w-0 flex-1">
          <span class="truncate leading-tight">{{ file.filename }}</span>
          <span class="text-[10px] text-n-slate-9 leading-tight">{{ formatFileSize(file.byte_size) }}</span>
        </div>
        <button
          class="flex-shrink-0 opacity-0 group-hover:opacity-100 hover:text-ruby-11 transition-all"
          @click="removeFile(index)"
        >
          <i class="i-lucide-x size-3" />
        </button>
      </div>
    </div>

    <!-- Upload progress indicator -->
    <div
      v-if="isUploading"
      class="flex items-center gap-2 mb-1 px-2 py-1 text-xs text-n-slate-11"
    >
      <i class="i-lucide-loader-2 size-3.5 animate-spin" />
      <span>{{ t('CAPTAIN.COPILOT.FILE_INPUT.UPLOADING') }}</span>
    </div>

    <form class="relative flex items-end gap-1" @submit.prevent="sendMessage">
      <!-- Hidden file input (accepts ALL file types) -->
      <input
        ref="fileInputRef"
        type="file"
        class="hidden"
        multiple
        @change="handleFileSelect"
      />

      <!-- Action buttons (attach + mic) -->
      <div class="flex items-center gap-0.5 pb-1.5">
        <button
          type="button"
          class="h-8 w-8 flex items-center justify-center rounded transition-colors"
          :class="
            canAttachMore
              ? 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2'
              : 'text-n-slate-8 cursor-not-allowed'
          "
          :title="t('CAPTAIN.COPILOT.FILE_INPUT.ATTACH_FILE')"
          :disabled="!canAttachMore"
          @click="openFilePicker"
        >
          <i class="i-lucide-paperclip size-4" />
        </button>
        <button
          v-if="speechSupported"
          type="button"
          class="h-8 w-8 flex items-center justify-center rounded transition-colors"
          :class="
            isRecording
              ? 'text-ruby-11 bg-ruby-3 animate-pulse'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2'
          "
          :title="
            isRecording
              ? t('CAPTAIN.COPILOT.AUDIO_INPUT.STOP_RECORDING')
              : t('CAPTAIN.COPILOT.AUDIO_INPUT.START_RECORDING')
          "
          @click="toggleRecording"
        >
          <i :class="isRecording ? 'i-lucide-mic-off size-4' : 'i-lucide-mic size-4'" />
        </button>
        <button
          type="button"
          class="h-8 w-8 flex items-center justify-center rounded transition-colors text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
          :title="t('CAPTAIN.COPILOT.IMAGE.GENERATE')"
          @click="emit('generateImage')"
        >
          <i class="i-lucide-image-plus size-4" />
        </button>
      </div>

      <!-- Textarea -->
      <div class="relative flex-1">
        <textarea
          ref="textareaRef"
          v-model="message"
          :placeholder="$t('CAPTAIN.COPILOT.SEND_MESSAGE')"
          class="w-full reset-base bg-n-alpha-3 ltr:pl-3 ltr:pr-10 rtl:pl-10 rtl:pr-3 py-3 text-sm border border-n-weak rounded-lg focus:outline-0 focus:outline-none focus:ring-2 focus:ring-n-blue-11 focus:border-n-blue-11 resize-none overflow-hidden max-h-[200px] mb-0 text-n-slate-12"
          rows="1"
          @input="handleInput"
          @keydown.enter.exact.prevent="sendMessage"
        />
        <button
          class="absolute ltr:right-1 rtl:left-1 top-1/2 -translate-y-1/2 h-9 w-10 flex items-center justify-center transition-colors"
          :class="
            hasContent && !isUploading
              ? 'text-n-blue-11 hover:text-n-blue-12'
              : 'text-n-slate-10'
          "
          :disabled="isUploading"
          type="submit"
        >
          <i class="i-ph-arrow-up" />
        </button>
      </div>
    </form>
  </div>
</template>
